import { Layout, Button, Avatar, Badge, Space, Input, Dropdown, Switch, Tooltip } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  BellOutlined,
  SearchOutlined,
  LogoutOutlined,
  BulbOutlined,
  BulbFilled,
  CheckOutlined,
  HighlightOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { AutoComplete, Typography } from 'antd';
import { AppstoreOutlined, SettingOutlined as CogOutlined } from '@ant-design/icons';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../utils/config';
import { trFold } from '../../utils/trText';

const { Header } = Layout;

// Üst arama: lokasyon_durum id'si → sıralama önceliği (Aktif, Kurulum Bekliyor, diğerleri).
const DURUM_ONCELIK = { 3: 0, 2: 1 };
const LOK_SINIR = 30;
const PLAT_SINIR = 10;
const MENU_SINIR = 8;

const decodeMessage = (text) => {
  if (!text) return '';
  try {
    let processed = text.replace(/\+/g, ' ');
    let decoded = decodeURIComponent(processed);
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    
    return decoded;
  } catch (e) {
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    } catch (e2) {
      return text;
    }
  }
};

const HeaderComponent = ({ collapsed, toggle, routePaths }) => {
  const { darkMode, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const API_BASE_URL = SHARED_API_BASE_URL;
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [bulkLocations, setBulkLocations] = useState([]);
  const [bulkPlatforms, setBulkPlatforms] = useState([]);
  const [menuRows, setMenuRows] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const PP_BASE_URL = 'https://dosya.atmturkiye.com';
  const BUCKET_NAME = 'atmpoint';
  const FOLDER_PATH = 'personel';
  const generateLogoUrl = (user_id) => {
    if (!user_id) {
      return null;
    }
    const url = `${PP_BASE_URL}/${BUCKET_NAME}/${FOLDER_PATH}%2F${user_id}.jpg`;    
    return url;
  };

  const handleLogout = () => {
    navigate('/giris?logout=true');
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const auth_token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/notifications/unread`, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.data || []);
        setNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [API_BASE_URL]);

  const markNotificationRead = useCallback(async (notificationId) => {
    try {
      const auth_token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${auth_token}`,
        },
      });
            
      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setNotificationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [API_BASE_URL]);

  React.useEffect(() => {
    const loadBulkData = async () => {
      try {
        const [locResp, platResp] = await Promise.all([
          fetch(`${API_BASE_URL}/atm/bulk_locations`),
          fetch(`${API_BASE_URL}/atm/bulk_platforms`)
        ]);
        
        if (locResp.ok && platResp.ok) {
          const locData = await locResp.json();
          const platData = await platResp.json();
          
          setBulkLocations(locData.data || []);
          setBulkPlatforms(platData.data || []);
          setDataLoaded(true);
          
        } else {
          console.error('Failed to load bulk data');
        }
      } catch (error) {
        console.error('Error loading bulk data:', error);
      }
    };

    loadBulkData();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 90000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const userMenuItems = [
    {
      key: '1',
      icon: <UserOutlined />,
      label: 'Profil',
      onClick: () => {
        navigate('/profil');
      },
    },
    {
      key: '2',
      icon: <HighlightOutlined />,
      label: 'Görevler',
      onClick: () => {
        navigate('/gorev');
      },
    },
    {
      type: 'divider',
    },
    {
      key: '3',
      icon: <LogoutOutlined />,
      label: 'Çıkış',
      onClick: handleLogout,
    },
  ];

  const notificationItems = notifications.length === 0 
    ? [
        {
          key: 'empty',
          label: (
            <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
              Yeni bildirim yok
            </div>
          ),
          disabled: true,
        },
      ]
    : notifications.map((notif) => ({
        key: notif.id,
        label: (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'space-between',
              gap: isMobile ? 6 : 8,
              maxWidth: isMobile ? 'calc(100vw - 80px)' : 300,
              padding: isMobile ? '4px 0' : '0',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontWeight: 600, 
                marginBottom: isMobile ? 2 : 4,
                fontSize: isMobile ? 13 : 14,
              }}>
                {notif.title}
              </div>
              <div
                style={{
                  fontSize: isMobile ? 11 : 12,
                  color: '#666',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: isMobile ? 1.3 : 1.5,
                }}
              >
                {(() => {
                  const decodedMessage = decodeMessage(notif.message);
                  const hasHtml = /<[^>]*>/.test(decodedMessage);

                  if (isMobile && decodedMessage.length > 60) {
                    const plainText = decodedMessage.replace(/<[^>]*>/g, '');
                    return plainText.length > 60 ? plainText.substring(0, 60) + '...' : plainText;
                  }

                  if (hasHtml) {
                    return <div dangerouslySetInnerHTML={{ __html: decodedMessage }} />;
                  }

                  return decodedMessage;
                })()}
              </div>
              <div style={{ 
                fontSize: isMobile ? 10 : 11, 
                color: '#999', 
                marginTop: isMobile ? 2 : 4 
              }}>
                {notif.eklenmeTarihi ? new Date(notif.eklenmeTarihi).toLocaleString('tr-TR', {
                  ...(isMobile ? {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  } : {})
                }) : ''}
              </div>
            </div>
            <CheckOutlined 
              style={{ 
                fontSize: isMobile ? 12 : 14, 
                color: '#52c41a',
                cursor: 'pointer',
                flexShrink: 0,
                marginTop: isMobile ? 2 : 0,
              }}
              onClick={(e) => {
                e.stopPropagation();
                markNotificationRead(notif.id);
              }}
            />
          </div>
        ),
      }));

  // Menü sonuçları kenar menüsüyle aynı kaynaktan (/feed/side-menu, kullanıcının
  // yetkisine göre süzülmüş) gelir. Önceden elle yazılmış 17 kalemlik bir liste
  // vardı ve İK, Toplantı, Rehber, Kayıt Ekle gibi kalemlerin çoğu /home'a gidiyordu.
  // Uygulamada rotası olmayan menü kayıtları (ör. /bkm-guncelleme) atlanır.
  useEffect(() => {
    let iptal = false;
    fetch(`${API_BASE_URL}/feed/side-menu`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!iptal && d?.success) setMenuRows(d.sideMenu || []); })
      .catch((e) => console.error('Arama menüsü yüklenemedi:', e));
    return () => { iptal = true; };
  }, [API_BASE_URL]);

  const routeKey = (routePaths || []).join('|');
  const menuIndex = useMemo(() => {
    const bilinen = new Set(routeKey ? routeKey.split('|') : []);
    const sirali = [...menuRows].sort((a, b) => a.id - b.id);
    // side_menu'de grup başlığı, aynı `parent` değerine sahip ilk (en küçük id'li) kayıttır.
    const grupAdi = {};
    for (const m of sirali) if (!(m.parent in grupAdi)) grupAdi[m.parent] = m.name;
    const goruldu = new Set();
    const out = [];
    for (const m of sirali) {
      const route = (m.url_path || '').trim();
      if (!route.startsWith('/') || goruldu.has(route)) continue;
      if (bilinen.size && !bilinen.has(route)) continue;
      goruldu.add(route);
      const grup = grupAdi[m.parent] && grupAdi[m.parent] !== m.name ? grupAdi[m.parent] : '';
      out.push({
        key: `menu:${route}`, label: m.name, grup, route,
        ad: trFold(m.name), anahtar: trFold(`${m.name} ${grup}`),
      });
    }
    return out;
  }, [menuRows, routeKey]);

  // Arama indeksi bulk veri gelince bir kez hazırlanır; her tuşta yalnız hazır
  // anahtarlar taranır. Alanlar: ad + PYP + il + ilçe + MaptriksID (adres gürültü
  // yaptığı için aranmaz).
  const locIndex = useMemo(() => {
    const out = [];
    for (const item of bulkLocations || []) {
      const id = String(item.MaptriksID || item.maptriksID || item.id || '').trim();
      const name = item.Name || item['Lokasyon Adı'] || item.name || '';
      if (!id || !name) continue;
      const pyp = (item.PYP || '').trim();
      const city = item.il_ad || '';
      const district = item.ilce_ad || '';
      const ad = trFold(`${name} ${pyp}`);
      out.push({
        id, name, pyp, city, district,
        firm: item.firma_adi || '',
        status: item.durum_name || 'Bilinmeyen',
        oncelik: DURUM_ONCELIK[Number(item.durum)] ?? 2,
        ad,
        anahtar: `${trFold(`${name} ${pyp} ${city} ${district}`)} ${id}`,
        item,
      });
    }
    return out;
  }, [bulkLocations]);

  const platIndex = useMemo(() => {
    const out = [];
    for (const item of bulkPlatforms || []) {
      const id = String(item.id || '').trim();
      if (!id) continue;
      const qr = Number(item.qr) > 0 ? String(item.qr) : '';
      const name = item.name || `Platform: ${id}`;
      const tip = item.tip || '';
      const ad = trFold(name);
      out.push({ id, qr, name, tip, ad, anahtar: trFold(`${name} ${qr ? `kn${qr}` : ''} ${tip}`), item });
    }
    return out;
  }, [bulkPlatforms]);

  const buildOptions = useCallback((query) => {
    const q = trFold(query);
    if (!q) return [];
    const kelimeler = q.split(' ');
    const ilk = kelimeler[0];

    // Tüm kelimeler (sırasız) anahtarda geçmeli. Kademe: 0 tam eşleşme (numara/ad/PYP),
    // 1 her kelime bir kelimenin başında (ad, il, ilçe), 2 kelime ortasında geçiyor.
    // Kademe içinde aktif → kurulum bekliyor → diğerleri; sonra ad yakınlığı
    // (aranan metinle başlıyor > bir kelimesi ilk kelimeyle başlıyor), sonra en yeni kayıt.
    const ara = (liste, tamMi, oncelikVar) => {
      const bulunan = [];
      for (let i = 0; i < liste.length; i++) {
        const x = liste[i];
        let uygun = true;
        for (const k of kelimeler) {
          if (x.anahtar.indexOf(k) === -1) { uygun = false; break; }
        }
        if (!uygun) continue;
        const anahtar = ` ${x.anahtar}`;
        const kademe = tamMi(x) ? 0 : kelimeler.every((k) => anahtar.includes(` ${k}`)) ? 1 : 2;
        const yakinlik = x.ad.startsWith(q) ? 0 : (` ${x.ad}`).includes(` ${ilk}`) ? 1 : 2;
        bulunan.push({ x, kademe, oncelik: oncelikVar ? x.oncelik : 0, yakinlik, sira: i });
      }
      bulunan.sort((a, b) => a.kademe - b.kademe || a.oncelik - b.oncelik
        || a.yakinlik - b.yakinlik || a.sira - b.sira);
      return bulunan;
    };

    const menuBul = ara(menuIndex, () => false, false);
    const lokBul = q.length < 2 ? [] : ara(locIndex, (x) => x.id === q || x.ad === q || trFold(x.pyp) === q, true);
    // 'platform 12' → yalnız 12 tam eşleşme sayılır, 120/1200 değil.
    const platBul = q.length < 2 ? [] : ara(platIndex,
      (x) => x.id === q || (x.qr && (x.qr === q || `kn${x.qr}` === q)) || x.ad === q || x.ad.startsWith(`${q} `), false);

    const fazlasi = (kalan, tur) => ({
      value: `more:${tur}`,
      disabled: true,
      label: (
        <span style={{ fontSize: 12, opacity: 0.6 }}>
          {kalan.toLocaleString('tr-TR')} sonuç daha var, aramayı daraltın
        </span>
      ),
    });
    const grup = (baslik, bulunan, sinir, tur, render) => {
      if (!bulunan.length) return null;
      const options = bulunan.slice(0, sinir).map(({ x }) => render(x));
      if (bulunan.length > sinir) options.push(fazlasi(bulunan.length - sinir, tur));
      const sayi = bulunan.length > sinir ? `${sinir} / ${bulunan.length.toLocaleString('tr-TR')}` : bulunan.length;
      return { label: `${baslik} (${sayi})`, options };
    };

    const grouped = [];

    // Aktif listede olmayan bir MaptriksID de doğrudan açılabilsin.
    if (/^\d{6,10}$/.test(q) && !locIndex.some((x) => x.id === q)) {
      grouped.push({
        label: '⚡ Hızlı Erişim',
        options: [{
          value: `quick:${q}`,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SearchOutlined />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 500 }}>Lokasyon Detayı</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>📍 MaptriksID: {q}</span>
              </div>
            </div>
          ),
          meta: { type: 'lok', id: q }
        }]
      });
    }

    grouped.push(grup('📋 Menüler', menuBul, MENU_SINIR, 'menu', (m) => ({
      value: m.key,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppstoreOutlined />
          <span>{m.label}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>{m.grup || 'Menü'}</span>
        </div>
      ),
      meta: { type: 'menu', route: m.route }
    })));

    grouped.push(grup('📍 Lokasyonlar', lokBul, LOK_SINIR, 'lok', (x) => ({
      value: `lok:${x.id}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SearchOutlined />
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 500, whiteSpace: 'normal' }}>{x.name}</span>
            <span style={{ fontSize: 12, opacity: 0.7, whiteSpace: 'normal' }}>
              {x.city} {x.district} • {x.firm} • <span style={{ color: x.oncelik === 0 ? '#52c41a' : undefined, fontWeight: x.oncelik === 0 ? 600 : undefined }}>{x.status}</span>
            </span>
            <span style={{ fontSize: 11, opacity: 0.5 }}>📍 ID: {x.id}{x.pyp ? ` • PYP: ${x.pyp}` : ''}</span>
          </div>
        </div>
      ),
      meta: { type: 'lok', id: x.id, name: x.name, fullData: x.item }
    })));

    grouped.push(grup('🏢 Platformlar', platBul, PLAT_SINIR, 'plat', (x) => ({
      value: `plat:${x.id}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SearchOutlined />
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <span style={{ fontWeight: 500 }}>{x.name}</span>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {x.tip || 'Platform'} • ID: {x.id}
            </span>
          </div>
        </div>
      ),
      meta: { type: 'plat', id: x.id, name: x.name, fullData: x.item }
    })));

    return grouped.filter(Boolean);
  }, [menuIndex, locIndex, platIndex]);

  const handleSearchChange = useCallback((val) => {
    setSearchValue(val);
    if (!dataLoaded) {
      setOptions([]);
      return;
    }
    setOptions(buildOptions(val));
  }, [dataLoaded, buildOptions]);

  const handleSelect = useCallback((value, option) => {
    const meta = option?.meta;
    if (!meta) return;
    
    const iconMap = {
      'lok': '📍',
      'menu': '📋', 
      'plat': '🏢'
    };
    
    const icon = iconMap[meta.type] || '❓';
    
    if (meta.type === 'lok' && meta.id) {
      navigate(`/detay/${meta.id}`);
      return;
    }
    if (meta.type === 'menu' && meta.route) {
      navigate(meta.route);
      return;
    }
    if (meta.type === 'plat' && meta.id) {
      navigate(`/platform_detay/${meta.id}`);
      return;
    }
  }, [navigate]);

  return (
    <Header 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: isMobile ? '0 8px' : '0 24px',
        justifyContent: 'space-between', 
        background: '#fff' 
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flex: isMobile ? '1' : 'initial' }}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggle}
          style={{ 
            fontSize: isMobile ? '14px' : '16px', 
            width: isMobile ? 48 : 64, 
            height: isMobile ? 48 : 64,
            color: darkMode ? 'var(--dark-text)' : undefined 
          }}
        />
        {!isMobile && (
          <AutoComplete
            value={searchValue}
            options={options}
            onSearch={handleSearchChange}
            onSelect={handleSelect}
            filterOption={false}
            // Enter, aktif seçeneği açar: varsayılan ilk sonuç, ok tuşlarıyla seçilen başka bir sonuç.
            defaultActiveFirstOption
            style={{ width: 320, marginLeft: 16 }}
          >
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: darkMode ? 'rgba(255, 255, 255, 0.45)' : undefined }} />}
              placeholder={dataLoaded ? "Lokasyon, platform veya menü ara..." : "Veriler yükleniyor..."}
              disabled={!dataLoaded}
              style={{ 
                backgroundColor: darkMode ? '#141414' : undefined,
                borderColor: darkMode ? '#303030' : undefined,
                color: darkMode ? 'var(--dark-text)' : undefined,
                opacity: dataLoaded ? 1 : 0.6
              }}
            />
          </AutoComplete>
        )}
      </div>
      
      <Space size={isMobile ? 4 : 16}>
        <Tooltip title={darkMode ? 'Aydınlık Tema' : 'Karanlık Tema'}>
          <Switch
            checked={darkMode}
            onChange={toggleTheme}
            checkedChildren={isMobile ? null : <BulbFilled />}
            unCheckedChildren={isMobile ? null : <BulbOutlined />}
            size={isMobile ? 'small' : 'default'}
          />
        </Tooltip>
        
        <Badge 
          count={notificationCount} 
          size={isMobile ? 'small' : 'default'}
          offset={isMobile ? [-2, 2] : [0, 0]}
        >
          <Dropdown 
            menu={{ items: notificationItems }} 
            placement="bottomRight"
            arrow
            trigger={['click']}
            overlayStyle={{ 
              maxWidth: isMobile ? 'calc(100vw - 40px)' : 320,
              width: isMobile ? 'calc(100vw - 40px)' : 'auto',
            }}
          >
            <Button 
              type="text" 
              icon={<BellOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              style={{
                fontSize: isMobile ? '14px' : '16px',
              }}
            />
          </Dropdown>
        </Badge>
        <Dropdown 
          menu={{ items: userMenuItems }} 
          placement="bottomRight"
          arrow
        >
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8, minWidth: 0 }}>
            <img 
              src={user?.kullanici_no ? generateLogoUrl(user.kullanici_no) : null}
              alt="Profil"
              style={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%',
                objectFit: 'cover',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9',
                flexShrink: 0
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallbackDiv = document.createElement('div');
                fallbackDiv.innerHTML = '<UserOutlined />';
                fallbackDiv.style.cssText = `
                  width: 32px; 
                  height: 32px; 
                  border-radius: 50%; 
                  background-color: #1890ff; 
                  display: flex; 
                  align-items: center; 
                  justify-content: center; 
                  color: white; 
                  font-size: 16px;
                  border: 1px solid #d9d9d9;
                `;
                fallbackDiv.setAttribute('data-fallback', 'true');
                const existingFallback = e.target.parentNode.querySelector('[data-fallback]');
                if (existingFallback) {
                  existingFallback.remove();
                }
                
                e.target.parentNode.insertBefore(fallbackDiv, e.target);
              }}
            />
            {!isMobile && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  minWidth: 0,
                  lineHeight: 1.35,
                }}
              >
                <span style={{ fontSize: 12, opacity: 0.9, color: darkMode ? 'var(--dark-text)' : undefined }}>
                  Hoş Geldiniz
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: darkMode ? 'var(--dark-text)' : undefined,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: 160,
                  }}
                >
                  {user?.ad_soyad || 'Kullanıcı'}
                </span>
              </div>
            )}
          </div>
        </Dropdown>
      </Space>
    </Header>
  );
};

export default HeaderComponent;
